import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';

import { PasswordFailureKey, ValidationService } from 'us-web-services';
import { Button, ButtonType } from '../Button';
import { useSharedTranslation } from '../hooks/useSharedTranslation';
import { PasswordInput } from '../PasswordInput';
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator';

interface Props {
  hasExistingPassword: boolean;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  onCurrentPasswordChange: (val: string) => void;
  onNewPasswordChange: (val: string) => void;
  onConfirmPasswordChange: (val: string) => void;
  onCancel: () => void;
  namespace?: string;
}

const REQUIREMENTS: Array<{ key: string; failureKey: PasswordFailureKey }> = [
  { key: 'minLength', failureKey: 'tooShort' },
  { key: 'lowercase', failureKey: 'noLower' },
  { key: 'uppercase', failureKey: 'noUpper' },
  { key: 'number', failureKey: 'noDigit' },
  { key: 'symbol', failureKey: 'noSymbol' },
];

export const PasswordChangeForm = ({
  hasExistingPassword,
  currentPassword,
  newPassword,
  confirmPassword,
  onCurrentPasswordChange,
  onNewPasswordChange,
  onConfirmPasswordChange,
  onCancel,
  namespace,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespace);
  const showMismatch =
    !!newPassword && newPassword.length >= 8 && confirmPassword !== newPassword;
  const failures = ValidationService.passwordFailures(newPassword);
  return (
    <div className='space-y-2'>
      <p className='italic text-sm'>{t('components.passwordChange.requirements')}</p>
      <ul className='list-none pl-0 text-sm space-y-1'>
        {REQUIREMENTS.map(({ key, failureKey }) => {
          const met = !failures.includes(failureKey);
          return (
            <li key={key} className={`flex items-center gap-2 ${met ? 'text-green-600' : 'text-neutral-600'}`}>
              <FontAwesomeIcon icon={met ? faCheck : faXmark} className={met ? 'text-green-600' : 'text-red-500'} />
              <span>{t(`components.passwordChange.requirementsList.${key}`)}</span>
            </li>
          );
        })}
      </ul>
      {hasExistingPassword && (
        <PasswordInput
          id='existing-password-input'
          aria-label={t('components.passwordChange.currentPasswordAriaLabel')}
          className='form-control w-full border border-neutral-300 rounded px-3 py-2'
          placeholder={t('components.passwordChange.currentPasswordPlaceholder')}
          value={currentPassword}
          onChange={(e): void => onCurrentPasswordChange(e.target.value)}
        />
      )}
      <PasswordInput
        id='account-settings-user-password'
        className='form-control w-full border border-neutral-300 rounded px-3 py-2'
        placeholder={t('components.passwordChange.newPasswordPlaceholder')}
        value={newPassword}
        onChange={(e): void => onNewPasswordChange(e.target.value)}
      />
      <PasswordStrengthIndicator password={newPassword} namespace={namespace} />
      <PasswordInput
        id='account-settings-user-password-repeat'
        className='form-control w-full border border-neutral-300 rounded px-3 py-2'
        placeholder={t('components.passwordChange.repeatPasswordPlaceholder')}
        value={confirmPassword}
        onChange={(e): void => onConfirmPasswordChange(e.target.value)}
      />
      {showMismatch && (
        <p className='text-red-600 text-sm'>
          {t('components.passwordChange.passwordsDoNotMatch')}
        </p>
      )}
      <Button
        buttonType={ButtonType.tertiary}
        id='cancel-edit-password'
        className='italic underline text-sm block text-right cursor-pointer bg-transparent border-0 p-0 ml-auto'
        onClick={onCancel}
      >
        {t('components.passwordChange.cancel')}
      </Button>
    </div>
  );
};
